import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';
import { TextComponent } from 'design/text/textComponents';
import {
  PartnerProfileFormValues,
  PartnerProfileFormViolation,
} from 'types/partnerProfileFormTypes';
import { createPartnerProfileFormViolationLense } from 'utils/validatePartnerProfileFormValues';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';
import { CountryInputComponent } from 'containers/CountryInputComponent';
import { LabelComponent } from 'design/label';
import { AvatarInputComponent } from 'design/avatarInput';
import { PartnerProfile } from 'types/state';
import { AvatarInputValue } from 'types/avatarInputTypes';

type Props = {
  prevProfile?: PartnerProfile;
  values: PartnerProfileFormValues;
  violations: PartnerProfileFormViolation[];
  disabled: boolean;
  onFirstNameChange(nextValue: string): void;
  onMiddleNameChange(nextValue: string): void;
  onLastNameChange(nextValue: string): void;
  onPhoneChange(nextValue: string): void;
  onCountryChange(nextValue: string): void;
  onCityChange(nextValue: string): void;
  onAvatarChange(nextValue: AvatarInputValue): void;
  onDetailsChange(nextValue: string): void;
};

const FIRST_NAME_MAX_LENGTH = 40;
const MIDDLE_NAME_MAX_LENGTH = 40;
const LAST_NAME_MAX_LENGTH = 40;
const PHONE_MAX_LENGTH = 19;
const CITY_MAX_LENGTH = 40;
const DETAILS_MAX_LENGTH = 2000;

const AVATAR_INPUT_ID = 'avatar-input-id';

export const PartnerProfileFieldset = (props: Props) => {
  const {
    disabled,
    prevProfile,
    values,
    violations,
    onFirstNameChange,
    onMiddleNameChange,
    onLastNameChange,
    onPhoneChange,
    onCountryChange,
    onCityChange,
    onAvatarChange,
    onDetailsChange,
  } = props;

  const {
    inputValidationRequired,
    partnerRegistrationManager: { profieFieldset: ownDictionary },
  } = useIntlDictionary();

  const lastNameHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'LastNameMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const lastNameIsInvalid = violations.some((violation) =>
    createPartnerProfileFormViolationLense(violation).isLastNameViolation()
  );

  const firstNameHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'FirstNameMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const firstNameIsInvalid = violations.some((violation) =>
    createPartnerProfileFormViolationLense(violation).isFirstNameViolation()
  );

  const middleNameHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'MiddleNameMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const middleNameIsInvalid = violations.some((violation) =>
    createPartnerProfileFormViolationLense(violation).isMiddleNameViolation()
  );

  const phoneHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'PhoneMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const phoneIsInvalid = violations.some((violation) =>
    createPartnerProfileFormViolationLense(violation).isPhoneViolation()
  );

  const countryHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'CountryMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const countryIsInvalid = violations.some((violation) =>
    createPartnerProfileFormViolationLense(violation).isCountryViolation()
  );

  const cityHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'CityMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const cityIsInvalid = violations.some((violation) =>
    createPartnerProfileFormViolationLense(violation).isCityViolation()
  );

  const avatarHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'AvatarMissed':
          return inputValidationRequired;
        case 'AvatarInputFileSize':
          return ownDictionary.avatarInputFileSizeViolationText;
        case 'AvatarCroppedFileSize':
          return ownDictionary.avatarCroppedFileSizeViolationText;
        case 'AvatarInputFileType':
          return ownDictionary.avatarFileTypeViolationText;
        case 'AvatarInputImageSize':
          return ownDictionary.avatarImageSizeViolationText;
      }
    }

    return ownDictionary.avatarRequirementsHintText;
  }, [
    ownDictionary.avatarRequirementsHintText,
    ownDictionary.avatarInputFileSizeViolationText,
    ownDictionary.avatarCroppedFileSizeViolationText,
    ownDictionary.avatarFileTypeViolationText,
    ownDictionary.avatarImageSizeViolationText,
    violations,
    inputValidationRequired,
  ]);

  const avatarValueIsInvalid = violations.some((violation) =>
    createPartnerProfileFormViolationLense(violation).isAvatarViolation()
  );

  const detailsHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'DetailsMissed':
          return inputValidationRequired;
      }
    }
  }, [inputValidationRequired, violations]);

  const detailsIsInvalid = violations.some((violation) =>
    createPartnerProfileFormViolationLense(violation).isDetailsViolation()
  );

  const prevAvatarURL = prevProfile?.avatarURL || null;

  return (
    <FieldsetLayoutComponent>
      <LabelComponent>
        <TextComponent color="primary" size="m">
          {ownDictionary.avatarInputLabelText}
        </TextComponent>
        <AvatarInputComponent
          id={AVATAR_INPUT_ID}
          placeholder={
            <TextComponent color="accent">
              {(values.avatar || prevAvatarURL) && !avatarValueIsInvalid
                ? ownDictionary.avatarInputPlaceholderText
                : ownDictionary.avatarInputNoValuePlaceholderText}
            </TextComponent>
          }
          name="avatar"
          fallbackURL={prevAvatarURL}
          value={values.avatar}
          disabled={disabled}
          invalid={avatarValueIsInvalid}
          hint={
            <TextComponent
              color={avatarValueIsInvalid ? 'critical' : 'secondary'}
            >
              {avatarHint}
            </TextComponent>
          }
          onValueChange={onAvatarChange}
        />
      </LabelComponent>
      <FloatingLabelComponent
        lifted={!!values.lastName}
        invalid={lastNameIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.lastNameInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {lastNameHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="lastName"
          autoFocus
          value={values.lastName}
          disabled={disabled}
          invalid={lastNameIsInvalid}
          maxLength={LAST_NAME_MAX_LENGTH}
          onValueChange={onLastNameChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.firstName}
        invalid={firstNameIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.firstNameInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {firstNameHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="firstName"
          value={values.firstName}
          disabled={disabled}
          invalid={firstNameIsInvalid}
          maxLength={FIRST_NAME_MAX_LENGTH}
          onValueChange={onFirstNameChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.middleName}
        invalid={middleNameIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.middleNameInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {middleNameHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="middleName"
          value={values.middleName}
          disabled={disabled}
          invalid={middleNameIsInvalid}
          maxLength={MIDDLE_NAME_MAX_LENGTH}
          onValueChange={onMiddleNameChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.phone}
        invalid={phoneIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.phoneInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {phoneHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="phone"
          value={values.phone}
          disabled={disabled}
          invalid={phoneIsInvalid}
          maxLength={PHONE_MAX_LENGTH}
          onValueChange={onPhoneChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.country}
        invalid={countryIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.countryInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {countryHint}
          </TextComponent>
        }
      >
        <CountryInputComponent
          name="country"
          value={values.country}
          invalid={countryIsInvalid}
          onChange={onCountryChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.city}
        invalid={cityIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.cityInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {cityHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="city"
          value={values.city}
          disabled={disabled}
          invalid={cityIsInvalid}
          maxLength={CITY_MAX_LENGTH}
          onValueChange={onCityChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.details}
        invalid={detailsIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.detailsInputLabelText}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {detailsHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="details"
          multiLine
          value={values.details}
          disabled={disabled}
          invalid={detailsIsInvalid}
          maxLength={DETAILS_MAX_LENGTH}
          onValueChange={onDetailsChange}
        />
      </FloatingLabelComponent>
    </FieldsetLayoutComponent>
  );
};
